import { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { ClientReferralWithUI, onClientConverted } from "@/api/clientReferrals";
import { ReferralInfoTab } from "@/components/referrals/tabs/ReferralInfoTab";
import { ClientNotesActionsTab } from "@/components/referrals/tabs/ClientNotesActionsTab";
import { ClientReferralRejectionModal, ClientReferralConvertedModal } from "@/components/admin";
import type { ClientConversionFormData } from "@/components/admin/ClientReferralConvertedModal";

// Client referral status type
type ClientReferralStatus = 'pending' | 'contacted' | 'meeting_scheduled' | 'converted' | 'rejected';

interface ClientReferralViewerProps {
  referral: ClientReferralWithUI | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusChange: (referralId: string, newStatus: ClientReferralStatus) => void;
  onAddNote: (referralId: string, note: string) => void;
}

export function ClientReferralViewer({ 
  referral, 
  isOpen, 
  onClose, 
  onStatusChange, 
  onAddNote 
}: ClientReferralViewerProps) {
  const [newNote, setNewNote] = useState("");
  const [activeTab, setActiveTab] = useState("referral-info");
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showConvertedModal, setShowConvertedModal] = useState(false);
  const [isProcessingConversion, setIsProcessingConversion] = useState(false);
  const { toast } = useToast();

  // Reset note and modals when modal closes or referral changes
  useEffect(() => {
    if (!isOpen) {
      setNewNote("");
      setShowRejectionModal(false);
      setShowConvertedModal(false);
      setIsProcessingConversion(false);
    }
  }, [isOpen]);

  // Reset active tab when referral changes
  useEffect(() => {
    if (referral) {
      setActiveTab("referral-info");
    }
  }, [referral?.id]);

  if (!referral) return null;

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      onAddNote(referral.id, newNote.trim());
      setNewNote("");
    }
  };

  const handleStatusChange = (newStatus: ClientReferralStatus) => {
    // Show confirmation modals for critical status changes
    if (newStatus === 'rejected') {
      setShowRejectionModal(true);
      return;
    }
    
    if (newStatus === 'converted') {
      setShowConvertedModal(true);
      return;
    }
    
    // For other statuses, proceed directly
    onStatusChange(referral.id, newStatus);
    
    // Show immediate feedback
    toast({
      title: "Status Updated",
      description: `${referral.companyName} status changed to ${newStatus.replace('_', ' ').toUpperCase()}.`
    });
  };

  // Modal handlers
  const handleConfirmRejection = () => {
    onStatusChange(referral.id, 'rejected');
    setShowRejectionModal(false);
    
    toast({
      title: "Client Referral Rejected",
      description: `${referral.companyName} has been marked as rejected.`
    });
  };

  const handleCancelRejection = () => {
    setShowRejectionModal(false);
  };

  const handleConfirmConversion = async (formData: ClientConversionFormData) => {
    setIsProcessingConversion(true);
    
    try {
      // Call Firebase function for client referral conversion
      // This will:
      // 1. Update the client referral status to 'converted'
      // 2. Automatically grant the reward to the referrer
      // 3. Create reward transaction records
      // 4. Send notification to referrer about reward availability
      // 5. Update any related analytics/tracking
      
      const result = await onClientConverted(referral.id);
      
      if (result.success) {
        // Update the status locally after successful conversion
        onStatusChange(referral.id, 'converted');
        setShowConvertedModal(false);
        setIsProcessingConversion(false);
        
        toast({
          title: "Client Converted! 🎉",
          description: result.message || `${referral.companyName} has been successfully converted. Reward has been granted to the referrer.`
        });
      } else {
        throw new Error(result.message || 'Conversion failed');
      }
    } catch (error: any) {
      console.error('Error converting client referral:', error);
      setIsProcessingConversion(false);
      
      // Handle specific error types
      let errorMessage = "Failed to convert client referral. Please try again.";
      
      if (error.message?.includes('not found') || error.message?.includes('404')) {
        errorMessage = "Firebase function not found. The onClientConverted function may not be deployed yet.";
      } else if (error.message?.includes('Client referral not found')) {
        errorMessage = "Client referral not found. Please refresh and try again.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Conversion Failed",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const handleCancelConversion = () => {
    setShowConvertedModal(false);
    setIsProcessingConversion(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'meeting_scheduled': return 'bg-purple-100 text-purple-800';
      case 'converted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      {/* Global Styles for Modal */}
      <style>{`
        .client-referral-viewer-wrapper {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .client-referral-viewer-wrapper::-webkit-scrollbar {
          display: none;
        }
        
        /* Additional scrollbar hiding for all browsers */
        .client-referral-viewer-wrapper *::-webkit-scrollbar {
          display: none;
        }
        .client-referral-viewer-wrapper * {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        /* Specifically target all scrollable areas in the modal */
        .client-referral-viewer-wrapper .overflow-y-auto,
        .client-referral-viewer-wrapper .overflow-x-auto,
        .client-referral-viewer-wrapper .overflow-auto {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .client-referral-viewer-wrapper .overflow-y-auto::-webkit-scrollbar,
        .client-referral-viewer-wrapper .overflow-x-auto::-webkit-scrollbar,
        .client-referral-viewer-wrapper .overflow-auto::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="client-referral-viewer-wrapper max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="space-grotesk text-xl font-bold">Client Referral Details</DialogTitle>
            <DialogDescription className="text-gray-600">
              Review client referral information and manage status updates.
            </DialogDescription>
          </DialogHeader>
        
          <div className="grid gap-6 py-4">
            {/* Header Section */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={undefined} />
                  <AvatarFallback className="text-lg space-grotesk bg-blue-100 text-blue-800">
                    {getInitials(referral.companyName)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 space-grotesk">{referral.companyName}</h2>
                  <p className="text-gray-600">{referral.pointOfContact}</p>
                  <p className="text-gray-500 text-sm">{referral.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium space-grotesk ${getStatusColor(referral.status)}`}>
                      {referral.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs Content */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 space-grotesk">
                <TabsTrigger value="referral-info">Referral Information</TabsTrigger>
                <TabsTrigger value="notes-actions">Notes & Actions</TabsTrigger>
              </TabsList>

              <TabsContent value="referral-info">
                <ReferralInfoTab referral={referral} />
              </TabsContent>

              <TabsContent value="notes-actions">
                <ClientNotesActionsTab 
                  referral={referral}
                  newNote={newNote}
                  onNewNoteChange={setNewNote}
                  onAddNote={handleAddNote}
                  onStatusChange={handleStatusChange}
                />
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejection Confirmation Modal */}
      <ClientReferralRejectionModal
        isOpen={showRejectionModal}
        companyName={referral.companyName}
        onConfirm={handleConfirmRejection}
        onCancel={handleCancelRejection}
      />

      {/* Conversion Confirmation Modal */}
      <ClientReferralConvertedModal
        isOpen={showConvertedModal}
        companyName={referral.companyName}
        rewardAmount={referral.rewardAmount}
        onConfirm={handleConfirmConversion}
        onCancel={handleCancelConversion}
        isProcessing={isProcessingConversion}
      />
    </>
  );
}
